import React, { useState, useEffect } from 'react';
import './WerkorderModal.css';
import api from '../services/api';
import ObjectKiezer from '../components/ObjectKiezer';
import OrdernummerKiezer from '../components/OrdernummerKiezer';
import InhuurNodig from '../components/InhuurNodig';
import PartijenBlok from '../components/PartijenBlok';
import { useAuth } from '../context/AuthContext';

const WerkorderModal = ({ show, onClose, onSuccess, werkorder = null, defaultDatum = null }) => {
  const { terms } = useAuth();
  const isEdit = !!werkorder;

  // Voorgevulde datum (bijv. vanuit bezettingskalender)
  useEffect(() => {
    if (show && !werkorder && defaultDatum) {
      setFormData(prev => ({ ...prev, geplande_datum: defaultDatum }));
    }
  }, [show, defaultDatum]);
  const [afdelingen, setAfdelingen] = useState([]);
  const [gebruikers, setGebruikers] = useState([]);
  const [klanten, setKlanten] = useState([]);
  const [projecten, setProjecten] = useState([]);
  const [koppelProjectId, setKoppelProjectId] = useState('');
  const [templates, setTemplates] = useState([]);
  const [selectedKlant, setSelectedKlant] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [bestaandeFotos, setBestaandeFotos] = useState([]);
  const [formData, setFormData] = useState({
    titel: '',
    ordernummer: '',
    sap_taak_nummer: '',
    beschrijving: '',
    afdeling_id: '',
    prioriteit: 'normaal',
    toegewezen_aan: [],
    geplande_datum: '',
    locatie_adres: '',
    verwachte_uren: 1,
    deadline: '',
    dienst_type: 'dag', herhaal_tot: '', herhaal_weekend: false, log_optioneel: false,
  });
  const [checklistItems, setChecklistItems] = useState([]);
  const [newChecklistItem, setNewChecklistItem] = useState({ beschrijving: '', type: 'checkbox' });
  const [selectedFotos, setSelectedFotos] = useState([]);
  const [fotoPreviews, setFotoPreviews] = useState([]);
  const [bestaandeBijlagen, setBestaandeBijlagen] = useState([]);
  const [selectedBijlagen, setSelectedBijlagen] = useState([]);
  const [mailBezig, setMailBezig] = useState(false);
  const [pendingInhuur, setPendingInhuur] = useState([]);
  const [pendingPartijen, setPendingPartijen] = useState({ partijen: [], datums: [] });
  const [scanning, setScanning] = useState(false);
  const [scanPdf, setScanPdf] = useState(null);
  const [gekozenObjecten, setGekozenObjecten] = useState([]);

  useEffect(() => {
    if (show) {
      loadData();
      if (isEdit) {
        setFormData({
          titel: werkorder.titel || '',
          ordernummer: werkorder.ordernummer || '',
          sap_taak_nummer: werkorder.sap_taak_nummer || '',
          beschrijving: werkorder.beschrijving || '',
          afdeling_id: werkorder.afdeling_id || '',
          prioriteit: werkorder.prioriteit || 'normaal',
          toegewezen_aan: werkorder.toegewezen_aan ? [werkorder.toegewezen_aan.toString()] : [],
          geplande_datum: werkorder.geplande_datum ? werkorder.geplande_datum.split('T')[0] : '',
          locatie_adres: werkorder.locatie_adres || '',
          verwachte_uren: werkorder.verwachte_uren || 1,
          deadline: werkorder.deadline ? werkorder.deadline.split('T')[0] : '',
          dienst_type: werkorder.dienst_type || 'dag',
          herhaal_tot: '', herhaal_weekend: false, // herhalen alleen bij aanmaken, niet bij bewerken
          log_optioneel: werkorder.log_optioneel || false,
        });
        loadWerkorderFotos(werkorder.id);
        api.get('/werkorders/' + werkorder.id + '/bijlagen').then(r => setBestaandeBijlagen(r.data?.bijlagen || [])).catch(() => {});
      } else {
        resetForm();
      }
    }
  }, [show, werkorder]);

  const resetForm = () => {
    setFormData({ titel: '', ordernummer: '', sap_taak_nummer: '', beschrijving: '', afdeling_id: '', prioriteit: 'normaal', toegewezen_aan: [], geplande_datum: '', locatie_adres: '', verwachte_uren: 1, deadline: '', dienst_type: 'dag', herhaal_tot: '', herhaal_weekend: false, log_optioneel: false });
    setChecklistItems([]);
    setGekozenObjecten([]);
    setSelectedFotos([]);
    setFotoPreviews([]);
    setSelectedKlant(null);
      setFormData({ ...formData, klant_id: null });
    setSelectedTemplate(null);
    setBestaandeFotos([]);
  };

  const loadData = async () => {
    try {
      const [afdelingenRes, gebruikersRes, klantenRes, templatesRes, projectenRes] = await Promise.all([
        api.get('/afdelingen'),
        api.get('/gebruikers'),
        api.get('/klanten'),
        api.get('/checklists/templates'),
        api.get('/projecten').catch(() => ({ data: {} }))
      ]);
      setProjecten((projectenRes.data && (projectenRes.data.projecten || projectenRes.data.data)) || []);
      setAfdelingen(afdelingenRes.data.afdelingen || []);
      setGebruikers(gebruikersRes.data.gebruikers || []);
      setKlanten(klantenRes.data.klanten || []);
      setTemplates(templatesRes.data.templates || []);
    } catch (error) {
      console.error('Fout bij laden data:', error);
    }
  };

  const loadWerkorderFotos = async (id) => {
    try {
      const res = await api.get(`/werkorders/${id}/fotos`);
      setBestaandeFotos(res.data.fotos || []);
    } catch (e) { console.error(e); }
  };

  const handleAfdelingChange = (afdelingId) => {
    setFormData({ ...formData, afdeling_id: afdelingId, toegewezen_aan: [] });
  };

  const handleKlantChange = (klantId) => {
    const klant = klanten.find(k => k.id == klantId);
    if (klant) {
      setSelectedKlant(klant);
      const volledigAdres = `${klant.adres || ''} ${klant.postcode || ''} ${klant.plaats || ''}`.trim();
      setFormData({ ...formData, locatie_adres: volledigAdres, klant_id: klant.id });
    } else {
      setSelectedKlant(null);
      setFormData({ ...formData, klant_id: null });
    }
  };

  const handleTemplateChange = async (templateId) => {
    if (!templateId) { setSelectedTemplate(null); return; }
    try {
      const response = await api.get(`/checklists/templates/${templateId}`);
      setSelectedTemplate(response.data.template);
      const items = response.data.items.map(item => ({ beschrijving: item.label, type: item.type }));
      setChecklistItems(items);
    } catch (error) { console.error('Fout bij laden template:', error); }
  };

  const addChecklistItem = () => {
    if (newChecklistItem.beschrijving.trim()) {
      setChecklistItems([...checklistItems, { ...newChecklistItem }]);
      setNewChecklistItem({ beschrijving: '', type: 'checkbox' });
    }
  };

  const removeChecklistItem = (index) => {
    setChecklistItems(checklistItems.filter((_, i) => i !== index));
  };

  const handleBijlageSelect = (e) => {
    const files = Array.from(e.target.files);
    setSelectedBijlagen(prev => [...prev, ...files]);
    e.target.value = '';
  };
  const removeBijlage = (idx) => setSelectedBijlagen(prev => prev.filter((_, i) => i !== idx));
  const mailBijlagen = async () => {
    if (!isEdit) { alert('Sla de werkorder eerst op voor je bijlagen mailt.'); return; }
    setMailBezig(true);
    try {
      const r = await api.post('/werkorders/' + werkorder.id + '/bijlagen/mail-mij');
      alert(r.data?.message || 'Bijlagen gemaild naar je eigen adres.');
    } catch (err) { alert(err.response?.data?.message || 'Mailen mislukt'); }
    setMailBezig(false);
  };
  const handleFotoSelect = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFotos([...selectedFotos, ...files]);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => { setFotoPreviews(prev => [...prev, { file, preview: reader.result }]); };
      reader.readAsDataURL(file);
    });
  };

  const removeFoto = (index) => {
    setSelectedFotos(selectedFotos.filter((_, i) => i !== index));
    setFotoPreviews(fotoPreviews.filter((_, i) => i !== index));
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    const prioriteitLabel = { laag: '🟢 Laag', normaal: '🟡 Normaal', hoog: '🔴 Hoog' }[formData.prioriteit] || formData.prioriteit;
    const afdeling = afdelingen.find(a => a.id == formData.afdeling_id);
    const monteurs = formData.toegewezen_aan.map(id => {
      const g = gebruikers.find(u => u.id == id);
      return g ? `${g.voornaam} ${g.achternaam}` : '';
    }).filter(Boolean).join(', ');

    printWindow.document.write(`<!DOCTYPE html><html><head><title>Werkorder ${formData.ordernummer || ''} - ${formData.titel}</title>
    <style>
      body { font-family: Arial, sans-serif; max-width: 800px; margin: 0 auto; padding: 30px; color: #333; }
      h1 { font-size: 22px; border-bottom: 3px solid #3b82f6; padding-bottom: 10px; }
      .header-row { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; }
      .logo { font-size: 18px; font-weight: bold; color: #3b82f6; }
      .datum { color: #666; font-size: 13px; }
      .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin: 16px 0; }
      .field { padding: 10px; background: #f8f9fa; border-radius: 6px; }
      .field-label { font-size: 11px; color: #666; text-transform: uppercase; font-weight: 600; }
      .field-value { font-size: 14px; font-weight: 500; margin-top: 2px; }
      .full-width { grid-column: 1 / -1; }
      .beschrijving { white-space: pre-wrap; line-height: 1.5; }
      .checklist { margin-top: 16px; }
      .checklist-item { padding: 6px 0; border-bottom: 1px solid #eee; display: flex; align-items: center; gap: 8px; }
      .checkbox { width: 16px; height: 16px; border: 2px solid #999; display: inline-block; }
      .fotos { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin-top: 10px; }
      .fotos img { width: 100%; border-radius: 6px; border: 1px solid #ddd; }
      @media print { body { padding: 15px; } }
    </style></head><body>
    <div class="header-row"><div class="logo">WOMSYS</div><div class="datum">Afgedrukt: ${new Date().toLocaleDateString('nl-NL')}</div></div>
    <h1>📋 Werkorder: ${formData.titel}</h1>
    <div class="grid">
      <div class="field"><div class="field-label">Ordernummer</div><div class="field-value">${formData.ordernummer || '-'}</div></div>
      <div class="field"><div class="field-label">Prioriteit</div><div class="field-value">${prioriteitLabel}</div></div>
      <div class="field"><div class="field-label">Afdeling</div><div class="field-value">${afdeling?.naam || '-'}</div></div>
      <div class="field"><div class="field-label">Verwachte uren</div><div class="field-value">${formData.verwachte_uren} uur</div></div>
      <div class="field"><div class="field-label">Geplande datum</div><div class="field-value">${formData.geplande_datum || '-'}</div></div>
      <div class="field"><div class="field-label">Deadline</div><div class="field-value">${formData.deadline || '-'}</div></div>
      <div class="field"><div class="field-label">Monteur(s)</div><div class="field-value">${monteurs || 'Niet toegewezen'}</div></div>
      <div class="field"><div class="field-label">Locatie</div><div class="field-value">${formData.locatie_adres || '-'}</div></div>
      ${formData.beschrijving ? `<div class="field full-width"><div class="field-label">Beschrijving</div><div class="field-value beschrijving">${formData.beschrijving}</div></div>` : ''}
    </div>
    ${checklistItems.length > 0 ? `<div class="checklist"><h3>📋 Checklist</h3>${checklistItems.map((item, i) => `<div class="checklist-item"><span class="checkbox"></span> ${i + 1}. ${item.beschrijving}</div>`).join('')}</div>` : ''}
    ${bestaandeFotos.length > 0 ? `<div><h3>📸 Foto's</h3><div class="fotos">${bestaandeFotos.map(f => `<img src="/api/werkorders/fotos/${f.id}/image" />`).join('')}</div></div>` : ''}
    </body></html>`);
    printWindow.document.close();
    setTimeout(() => printWindow.print(), 500);
  };

  const handlePdfScan = async (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setScanning(true);
    try {
      const fd = new FormData();
      fd.append('pdf', file);
      const res = await api.post('/opdracht-scan/scan', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const v = (res.data && res.data.voorstel) || {};
      setFormData(prev => ({ ...prev,
        titel: v.titel || prev.titel,
        beschrijving: v.beschrijving || prev.beschrijving,
        prioriteit: v.prioriteit || prev.prioriteit,
        deadline: v.deadline || prev.deadline,
        ordernummer: v.ordernummer || prev.ordernummer,
        sap_taak_nummer: v.sap_taak_nummer || prev.sap_taak_nummer
      }));
      setScanPdf(file);
    } catch (err) {
      alert((err.response && err.response.data && err.response.data.message) || 'Fout bij scannen PDF');
    }
    setScanning(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.titel || !formData.afdeling_id) {
      alert('Titel en afdeling zijn verplicht');
      return;
    }
    setSubmitting(true);
    try {
      let werkorderId;
      if (isEdit) {
        await api.put(`/werkorders/${werkorder.id}`, formData);
        werkorderId = werkorder.id;
      } else {
        const werkorderData = { ...formData, checklist: checklistItems, checklist_template_id: selectedTemplate?.id || null };
        const werkorderRes = await api.post('/werkorders', werkorderData);

      // Inhuur draft-regels koppelen (alleen bij CREATE, niet bij edit)
      const _newWoId = werkorderRes.data && (werkorderRes.data.id || (werkorderRes.data.werkorder && werkorderRes.data.werkorder.id));
      werkorderId = _newWoId;
      if (!werkorder && _newWoId && koppelProjectId) {
        try { await api.post(`/projecten/${koppelProjectId}/werkorders`, { werkorder_id: _newWoId }); }
        catch (err) { alert('Werkorder is aangemaakt, maar koppelen aan project is mislukt. Koppel hem handmatig via de projectpagina.'); }
      }
      if (!werkorder && scanPdf && werkorderId) {
        try {
          const bfd = new FormData();
          bfd.append('bijlage', scanPdf);
          await api.post(`/werkorders/${werkorderId}/bijlagen`, bfd, { headers: { 'Content-Type': 'multipart/form-data' } });
        } catch (err) { console.warn('Opdracht-PDF niet als bijlage opgeslagen:', err.message); }
        setScanPdf(null);
      }
      if (!werkorder && _newWoId && pendingPartijen.partijen.length > 0) {
        try {
          if (pendingPartijen.datums.length > 0) {
            await api.post(`/werkorder-partijen/${_newWoId}/voorstellen`, { datums: pendingPartijen.datums, duur_dagen: pendingPartijen.duur_dagen || 1, aanvang_tijd: pendingPartijen.aanvang_tijd || '' });
          }
          for (const p of pendingPartijen.partijen) {
            const pr = await api.post(`/werkorder-partijen/${_newWoId}/partijen`, p.type === 'afdeling'
              ? { partij_type: 'afdeling', afdeling_id: p.id }
              : { partij_type: 'extern', onderaannemer_id: p.id, opleverpunten: pendingPartijen.opleverpunten || '' });
            if (pendingPartijen.direct_versturen && p.type === 'extern' && pr.data && pr.data.partij) {
              try { await api.post(`/werkorder-partijen/partijen/${pr.data.partij.id}/verstuur`); }
              catch (err) { console.warn('Direct versturen mislukt:', err.message); }
            }
          }
        } catch (err) { console.warn('Partijen niet gekoppeld:', err.message); }
      }
      if (!werkorder && _newWoId && pendingInhuur.length > 0) {
        for (const row of pendingInhuur) {
          try {
            await api.post('/inhuur/bestellingen', {
              resource_id: row.resource_id,
              specificatie: row.specificatie || null,
              datum_van: row.datum_van,
              datum_tot: row.datum_tot,
              aantal: row.aantal || 1,
              opmerking: row.opmerking || null,
              werkorder_id: _newWoId
            });
          } catch (err) {
            console.warn('Inhuur-regel niet opgeslagen:', err.message);
          }
        }
      }
        werkorderId = werkorderRes.data.werkorder.id;
      }

      if (gekozenObjecten.length > 0 && werkorderId) {
        try {
          await api.post(`/werkorders/${werkorderId}/objecten`, { object_ids: gekozenObjecten.map(o => o.id) });
          // bij 2+ objecten + checklist-stappen: checklist PER OBJECT aanmaken (elk object eigen stappen)
          const stappen = checklistItems.map(it => (it.beschrijving || '').trim()).filter(Boolean);
          if (gekozenObjecten.length >= 2 && stappen.length > 0) {
            await api.post(`/werkorders/${werkorderId}/checklist-per-object`, { stappen });
          }
        } catch (err) { console.warn('Objecten/checklist-per-object:', err.message); }
      }

      if (selectedFotos.length > 0) {
        for (const foto of selectedFotos) {
          const fd = new FormData();
          fd.append('foto', foto);
          await api.post(`/werkorders/${werkorderId}/fotos`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        }
      }
      if (selectedBijlagen.length > 0 && werkorderId) {
        for (const best of selectedBijlagen) {
          const bfd = new FormData();
          bfd.append('bijlage', best);
          try { await api.post(`/werkorders/${werkorderId}/bijlagen`, bfd, { headers: { 'Content-Type': 'multipart/form-data' } }); }
          catch (err) { console.warn('Bijlage niet opgeslagen:', err.message); }
        }
      }

      resetForm();
      onSuccess();
      onClose();
    } catch (error) {
      alert(error.response?.data?.message || 'Fout bij opslaan werkorder');
    }
    setSubmitting(false);
  };

  if (!show) return null;

  const gefilterdGebruikers = formData.afdeling_id
    ? gebruikers.filter(g => g.afdeling_id == formData.afdeling_id && g.actief && !String(g.gebruikersnaam || '').startsWith('oa_'))
    : [];

  return (
    <div className="wo-modal-overlay">
      <div className="wo-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="wo-modal-header">
          <div className="wo-modal-header-left">
            <h2>{isEdit ? '✏️ Werkorder Bewerken' : '📋 Nieuwe Werkorder'}</h2>
            {isEdit && <span className="wo-id-badge">#{werkorder.id}</span>}
          </div>
          <div className="wo-modal-header-right">
            {!isEdit && (
              <label className="wo-btn wo-btn-print" style={{cursor: scanning ? 'wait' : 'pointer', opacity: scanning ? 0.6 : 1}}>
                {scanning ? '\u23f3 Scannen...' : (scanPdf ? '\ud83d\udcc4 ' + scanPdf.name.slice(0, 18) : '\ud83d\udcc4 PDF scannen')}
                <input type="file" accept="application/pdf" style={{display:'none'}} onChange={handlePdfScan} disabled={scanning} />
              </label>
            )}
            <button type="button" onClick={handlePrint} className="wo-btn wo-btn-print">🖨️ Print</button>
            <button onClick={onClose} className="wo-btn-close">×</button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="wo-form">
          {/* Sectie 1: Basis info */}
          <div className="wo-section">
            <div className="wo-section-title">📝 Basis Informatie</div>
            <div className="wo-grid-2">
              <div className="wo-field">
                <label>Ordernummer</label>
                <OrdernummerKiezer value={formData.ordernummer} afdelingId={formData.afdeling_id} onChange={(v) => setFormData(prev => ({...prev, ordernummer: v}))} />
              </div>
              <div className="wo-field">
                <label>SAP Taak-nummer <span style={{ fontWeight: 400, color: "#6b7280", fontSize: "12px" }}>(optioneel — voor TWI/VOS)</span></label>
                <input type="text" value={formData.sap_taak_nummer} onChange={(e) => setFormData({...formData, sap_taak_nummer: e.target.value})} placeholder="bv. 0042" maxLength={10} />
              </div>
              <div className="wo-field">
                <label>Prioriteit</label>
                <select value={formData.prioriteit} onChange={(e) => setFormData({...formData, prioriteit: e.target.value})}>
                  <option value="laag">🟢 Laag</option>
                  <option value="normaal">🟡 Normaal</option>
                  <option value="hoog">🔴 Hoog</option>
                </select>
              </div>
              <div className="wo-field">
                <label>Dienst</label>
                <select value={formData.dienst_type} onChange={(e) => setFormData({...formData, dienst_type: e.target.value})}>
                  <option value="dag">☀️ Dag</option>
                  <option value="nacht">🌙 Nacht</option>
                </select>
              </div>
            </div>
            <div className="wo-field">
              <label>Titel <span className="required">*</span></label>
              <input type="text" value={formData.titel} onChange={(e) => setFormData({...formData, titel: e.target.value})} placeholder="Bijv. Airco reparatie kantoor 2" required />
            </div>
            <div className="wo-field">
              <label>Beschrijving</label>
              <textarea value={formData.beschrijving} onChange={(e) => setFormData({...formData, beschrijving: e.target.value})} placeholder="Beschrijf de werkzaamheden..." rows="3" />
            </div>
            <div className="wo-field">
              <label>Objecten</label>
              <ObjectKiezer waarde={gekozenObjecten} onChange={setGekozenObjecten} />
            </div>
          </div>

          {/* Sectie 2: Planning & Toewijzing */}
          <div className="wo-section">
            <div className="wo-section-title">📅 Planning & Toewijzing</div>
            <div className="wo-grid-2">
              <div className="wo-field">
                <label>Afdeling <span className="required">*</span></label>
                <select value={formData.afdeling_id} onChange={(e) => handleAfdelingChange(e.target.value)} required>
                  <option value="">Selecteer afdeling</option>
                  {afdelingen.map(afd => <option key={afd.id} value={afd.id}>{afd.naam}</option>)}
                </select>
              </div>
              <div className="wo-field">
                <label>Verwachte tijd</label>
                <select value={formData.verwachte_uren} onChange={(e) => setFormData({...formData, verwachte_uren: parseFloat(e.target.value)})}>
                  <option value={0.5}>30 min</option>
                  <option value={1}>1 uur</option>
                  <option value={1.5}>1,5 uur</option>
                  <option value={2}>2 uur</option>
                  <option value={3}>3 uur</option>
                  <option value={4}>4 uur</option>
                  <option value={6}>6 uur</option>
                  <option value={8}>8 uur</option>
                </select>
              </div>
              <div className="wo-field">
                <label>Geplande datum</label>
                <input type="date" value={formData.geplande_datum} onChange={(e) => setFormData({...formData, geplande_datum: e.target.value})} />
              </div>
              <div className="wo-field">
                <label>Deadline</label>
                <input type="date" value={formData.deadline} onChange={(e) => setFormData({...formData, deadline: e.target.value})} />
              </div>
              <div className="wo-field">
                <label>🔁 Dagelijks herhalen t/m <span style={{ fontWeight: 400, color: '#64748b' }}>(elke dag een eigen werkorder)</span></label>
                <input type="date" value={formData.herhaal_tot}
                  min={formData.geplande_datum || undefined}
                  onChange={(e) => setFormData({...formData, herhaal_tot: e.target.value})} />
                {formData.herhaal_tot && (
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, fontSize: 13, fontWeight: 400, cursor: 'pointer' }}>
                    <input type="checkbox" checked={formData.herhaal_weekend}
                      onChange={(e) => setFormData({...formData, herhaal_weekend: e.target.checked})} />
                    ook in het weekend
                  </label>
                )}
              </div>
              {!werkorder && (
              <div className="wo-field">
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontWeight: 600 }}>
                  <input type="checkbox" checked={formData.log_optioneel}
                    onChange={(e) => setFormData({...formData, log_optioneel: e.target.checked})} />
                  Logboek niet verplicht bij afmelden <span style={{ fontWeight: 400, color: '#64748b' }}>(handig bij dagelijkse resets)</span>
                </label>
              </div>
              )}
            </div>
            <div className="wo-field">
              <label>Monteur(s) toewijzen {formData.toegewezen_aan.length > 0 && <span className="wo-badge-green">✓ {formData.toegewezen_aan.length} geselecteerd</span>}</label>
              <div className="wo-monteur-grid">
                {!formData.afdeling_id && <div className="wo-hint">Selecteer eerst een afdeling</div>}
                {gefilterdGebruikers.map(g => (
                  <label key={g.id} className={`wo-monteur-item ${formData.toegewezen_aan.includes(g.id.toString()) ? 'selected' : ''}`}>
                    <input type="checkbox" checked={formData.toegewezen_aan.includes(g.id.toString())}
                      onChange={(e) => {
                        const id = g.id.toString();
                        if (e.target.checked) setFormData({...formData, toegewezen_aan: [...formData.toegewezen_aan, id]});
                        else setFormData({...formData, toegewezen_aan: formData.toegewezen_aan.filter(x => x !== id)});
                      }} />
                    <span className="wo-monteur-name">{g.voornaam} {g.achternaam}</span>
                    <span className="wo-monteur-rol">{g.rol}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Sectie 3: Locatie */}
          <div className="wo-section">
            {!isEdit && projecten.length > 0 && (
              <div className="wo-field" style={{ marginBottom: '12px' }}>
                <label>📁 Koppelen aan project</label>
                <select value={koppelProjectId} onChange={(e) => setKoppelProjectId(e.target.value)}>
                  <option value="">— Geen project —</option>
                  {projecten.map(pr => <option key={pr.id} value={pr.id}>{pr.project_code ? pr.project_code + ' · ' : ''}{pr.naam}</option>)}
                </select>
              </div>
            )}
            <div className="wo-section-title">📍 Locatie</div>
            <div className="wo-grid-2">
              <div className="wo-field">
                <label>{terms?.klant_singular || "Klant"} selecteren</label>
                <select value={selectedKlant?.id || ''} onChange={(e) => handleKlantChange(e.target.value)}>
                  <option value="">Handmatig invoeren</option>
                  {klanten.map(klant => <option key={klant.id} value={klant.id}>{klant.naam} {klant.plaats && `- ${klant.plaats}`}</option>)}
                </select>
              </div>
              <div className="wo-field">
                <label>Adres</label>
                <input type="text" value={formData.locatie_adres} onChange={(e) => setFormData({...formData, locatie_adres: e.target.value})} placeholder="Straat 123, Plaats" />
              </div>
            </div>
          </div>

          {/* Sectie 4: Foto's */}
          <div className="wo-section">
            <div className="wo-section-title">📸 Foto's</div>
            {bestaandeFotos.length > 0 && (
              <div className="wo-fotos-grid">
                {bestaandeFotos.map((foto, i) => (
                  <div key={foto.id} className="wo-foto-item">
                    <img src={`/api/werkorders/fotos/${foto.id}/image`} alt={`Foto ${i + 1}`} />
                  </div>
                ))}
              </div>
            )}
            <div className="wo-foto-upload">
              <label className="wo-btn wo-btn-upload">📷 Camera <input type="file" accept="image/*" capture="environment" onChange={handleFotoSelect} hidden /></label>
              <label className="wo-btn wo-btn-upload">📁 Bestanden <input type="file" accept="image/*" multiple onChange={handleFotoSelect} hidden /></label>
            </div>
            {fotoPreviews.length > 0 && (
              <div className="wo-fotos-grid" style={{ marginTop: '12px' }}>
                {fotoPreviews.map((item, index) => (
                  <div key={index} className="wo-foto-item wo-foto-new">
                    <img src={item.preview} alt={`Nieuw ${index + 1}`} />
                    <button type="button" onClick={() => removeFoto(index)} className="wo-foto-remove">×</button>
                    <span className="wo-foto-new-badge">NIEUW</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Sectie 4b: Bestanden / bijlagen */}
          <div className="wo-section">
            <div className="wo-section-title">📎 Bestanden</div>
            {bestaandeBijlagen.length > 0 && (
              <div style={{ marginBottom: 8 }}>
                {bestaandeBijlagen.map((b) => (
                  <div key={b.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: '1px solid #eee' }}>
                    <span>📄</span>
                    <a href={b.url} target="_blank" rel="noopener noreferrer" style={{ flex: 1, color: '#2563eb', textDecoration: 'none', fontSize: 14 }}>{b.originele_naam || b.bestand_naam}</a>
                  </div>
                ))}
              </div>
            )}
            <div className="wo-foto-upload">
              <label className="wo-btn wo-btn-upload">📁 Bestand toevoegen <input type="file" multiple onChange={handleBijlageSelect} hidden /></label>
              {isEdit && bestaandeBijlagen.length > 0 && (
                <button type="button" className="wo-btn wo-btn-upload" onClick={mailBijlagen} disabled={mailBezig}>✉️ {mailBezig ? 'Mailen...' : 'Mail naar mij'}</button>
              )}
            </div>
            {selectedBijlagen.length > 0 && (
              <div style={{ marginTop: 8 }}>
                {selectedBijlagen.map((b, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0' }}>
                    <span>📄</span>
                    <span style={{ flex: 1, fontSize: 14 }}>{b.name}</span>
                    <button type="button" onClick={() => removeBijlage(i)} style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 18 }}>×</button>
                    <span style={{ fontSize: 11, background: '#dbeafe', color: '#1e40af', padding: '2px 6px', borderRadius: 4 }}>NIEUW</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Sectie 5: Checklist (alleen bij aanmaken) */}
          {!isEdit && (
            <div className="wo-section">
              <div className="wo-section-title">✅ Checklist</div>
              <div className="wo-field">
                <label>Template</label>
                <select value={selectedTemplate?.id || ''} onChange={(e) => handleTemplateChange(e.target.value)}>
                  <option value="">Geen template / Handmatig</option>
                  {templates.map(t => <option key={t.id} value={t.id}>{t.naam} ({t.aantal_items} items)</option>)}
                </select>
              </div>
              <div className="wo-checklist-add">
                <input type="text" value={newChecklistItem.beschrijving} onChange={(e) => setNewChecklistItem({...newChecklistItem, beschrijving: e.target.value})} onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addChecklistItem())} placeholder="Nieuw checklist item..." />
                <select value={newChecklistItem.type} onChange={(e) => setNewChecklistItem({...newChecklistItem, type: e.target.value})} className="wo-type-select">
                  <option value="checkbox">☑️</option>
                  <option value="text">📝</option>
                </select>
                <button type="button" onClick={addChecklistItem} className="wo-btn wo-btn-green">+ Toevoegen</button>
              </div>
              {checklistItems.length > 0 && (
                <div className="wo-checklist-items">
                  {checklistItems.map((item, index) => (
                    <div key={index} className="wo-checklist-item">
                      <span className="wo-cl-icon">{item.type === 'checkbox' ? '☑️' : '📝'}</span>
                      <span className="wo-cl-text">{index + 1}. {item.beschrijving}</span>
                      <button type="button" onClick={() => removeChecklistItem(index)} className="wo-cl-remove">×</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Footer */}
          {(!werkorder || werkorder.status !== 'afgerond') && (
            <div style={{ padding: '0 4px' }}>
              <InhuurNodig
                werkorderId={werkorder ? werkorder.id : null}
                onPendingChange={werkorder ? undefined : setPendingInhuur}
              />
              {!werkorder && (
                <PartijenBlok afdelingen={afdelingen} onPendingChange={setPendingPartijen} />
              )}
            </div>
          )}
          <div className="wo-modal-footer">
            <button type="button" onClick={onClose} className="wo-btn wo-btn-cancel">Annuleren</button>
            <button type="submit" disabled={submitting} className="wo-btn wo-btn-save">
              {submitting ? '⏳ Opslaan...' : isEdit ? '💾 Wijzigingen Opslaan' : '✅ Werkorder Aanmaken'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default WerkorderModal;

